import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AttachMoney,
  CalendarToday,
  Description,
  Person,
  Save,
  ArrowBack,
  TrendingUp,
  SwapHoriz,
} from '@mui/icons-material';
import { API_URL } from "../config";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const AddTopUp = () => {
    const [formData, setFormData] = useState({
        currency: 'USD',
        exchange_rate: 1,
        date: new Date().toISOString().split('T')[0],
        credit: '',
        debit: '',
        balance: '',
        description: '',
        client_profile_id: '',
    });
    
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isAdmin, setIsAdmin] = useState(true);

    useEffect(() => {
        const role = "Admin";
        if (role === 'Admin') {
            setIsAdmin(true);
        }
        if(role === '' || role === null){
            window.location.href = '/';
        }
    }, []);

    const navigate = useNavigate();

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.currency) newErrors.currency = 'Currency is required';
        if (!formData.exchange_rate || formData.exchange_rate <= 0) newErrors.exchange_rate = 'Exchange rate must be greater than 0';
        if (!formData.date) newErrors.date = 'Date is required';
        if (!formData.credit && !formData.debit) newErrors.amount = 'Either credit or debit is required';
        if (formData.credit && parseFloat(formData.credit) < 0) newErrors.credit = 'Credit must be positive';
        if (formData.debit && parseFloat(formData.debit) < 0) newErrors.debit = 'Debit must be positive';
        if (!formData.description) newErrors.description = 'Description is required';
        if (!formData.client_profile_id) newErrors.client_profile_id = 'Client ID is required';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const calculateBalance = () => {
        const credit = parseFloat(formData.credit) || 0;
        const debit = parseFloat(formData.debit) || 0;
        return credit - debit;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setLoading(true);
        
        try {
            const payload = {
                ...formData,
                credit: formData.credit || 0,
                debit: formData.debit || 0,
                balance: calculateBalance(),
                exchange_rate: parseFloat(formData.exchange_rate),
            };

            const response = await fetch(`${API_URL}/TopUp`, {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (response.status === 200) {
                Swal.fire({
                    title: "Success!",
                    text: "Top up saved successfully!",
                    icon: "success",
                    confirmButtonColor: "#3085d6",
                });
                navigate('/Topup');
            } else {
                throw new Error('Failed to save top up');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to save top up. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const currencies = ['USD', 'EUR', 'GBP', 'ZAR', 'BWP'];

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            bgcolor: 'background.default',
            p: 3
        }}>
            <Paper elevation={3} sx={{ maxWidth: 800, mx: 'auto', borderRadius: 2 }}>
                <Box sx={{ 
                    p: 3, 
                    bgcolor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: '12px 12px 0 0'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AccountBalanceWallet sx={{ fontSize: 32 }} />
                        <Typography variant="h4" component="h1" fontWeight="bold">
                            Top Up Account
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                            Add credit or debit transactions to client accounts
                        </Typography>
                </Box>

                <CardContent sx={{ p: 4 }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            {/* Row 1: Currency and Exchange Rate */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={!!errors.currency}>
                                    <InputLabel>Currency</InputLabel>
                                    <Select
                                        value={formData.currency}
                                        onChange={handleInputChange('currency')}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <AttachMoney />
                                            </InputAdornment>
                                        }
                                    >
                                        {currencies.map((curr) => (
                                            <MenuItem key={curr} value={curr}>
                                                {curr}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.currency && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                            {errors.currency}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Exchange Rate"
                                    type="number"
                                    value={formData.exchange_rate}
                                    onChange={handleInputChange('exchange_rate')}
                                    error={!!errors.exchange_rate}
                                    helperText={errors.exchange_rate}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <TrendingUp />
                                            </InputAdornment>
                                        ),
                                        inputProps: { min: 0, step: 0.01 }
                                    }}
                                />
                            </Grid>

                            {/* Row 2: Date and Client ID */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Date"
                                    type="date"
                                    value={formData.date}
                                    onChange={handleInputChange('date')}
                                    error={!!errors.date}
                                    helperText={errors.date}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CalendarToday />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Client ID"
                                    value={formData.client_profile_id}
                                    onChange={handleInputChange('client_profile_id')}
                                    error={!!errors.client_profile_id}
                                    helperText={errors.client_profile_id}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>

                            {/* Row 3: Credit and Debit */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Credit Amount"
                                    type="number"
                                    value={formData.credit}
                                    onChange={handleInputChange('credit')}
                                    error={!!errors.credit}
                                    helperText={errors.credit || "Enter amount to add"}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <TrendingUp sx={{ color: 'success.main' }} />
                                            </InputAdornment>
                                        ),
                                        inputProps: { min: 0, step: 0.01 }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Debit Amount"
                                    type="number"
                                    value={formData.debit}
                                    onChange={handleInputChange('debit')}
                                    error={!!errors.debit}
                                    helperText={errors.debit || "Enter amount to deduct"}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <TrendingUp sx={{ color: 'error.main', transform: 'rotate(180deg)' }} />
                                            </InputAdornment>
                                        ),
                                        inputProps: { min: 0, step: 0.01 }
                                    }}
                                />
                            </Grid>

                            {/* Row 4: Calculated Balance */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Calculated Balance"
                                    type="number"
                                    value={calculateBalance().toFixed(2)}
                                    disabled
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SwapHoriz />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                bgcolor: 'grey.100',
                                                borderRadius: 1
                                            }
                                        }
                                    }}
                                    helperText="Automatically calculated as Credit - Debit"
                                />
                            </Grid>

                            {/* Row 5: Description */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={3}
                                    value={formData.description}
                                    onChange={handleInputChange('description')}
                                    error={!!errors.description}
                                    helperText={errors.description || "Provide details about this transaction"}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 2 }}>
                                                <Description />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {/* Action Buttons */}
                        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBack />}
                                onClick={() => navigate('/Topup')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                                disabled={loading}
                                sx={{ minWidth: 120 }}
                            >
                                {loading ? 'Saving...' : 'Save Transaction'}
                            </Button>
                        </Box>
                    </form>
                </CardContent>
            </Paper>
        </Box>
    );
};

export default AddTopUp;